import React, { useState, useRef, useEffect } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Contact from '../components/Contact';
import { 
  ArrowRight, CheckCircle2, Sun, Zap, TrendingDown, Home, Shield, 
  Leaf, MessageCircle, X, Send, Calculator, ChevronDown, ChevronUp,
  Battery, BarChart3, Euro, Award
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Translations for Photovoltaik page
const pvTranslations = {
  de: {
    badge: 'Photovoltaik der Zukunft',
    heroTitle: 'Solarenergie für',
    heroTitle2: 'Ihr Zuhause',
    heroDesc: 'Entdecken Sie unsere Premium-Solarmodule von Aiko, Bosch und SIG Energy. Mit modernster Technologie und bis zu 25 Jahren Garantie.',
    freeConsult: 'Kostenlose Beratung',
    calcSavings: 'Ersparnis berechnen',
    yearsWarranty: 'Jahre Garantie',
    efficiency: 'Wirkungsgrad',
    installations: 'Installationen',
    premiumBrands: 'Unsere Premium Marken',
    premiumBrandsDesc: 'Wir arbeiten nur mit den besten Herstellern für Ihre Solaranlage',
    solarCalc: 'Solar-Rechner',
    calcPotential: 'Berechnen Sie Ihr Einsparpotenzial in wenigen Sekunden',
    roofArea: 'Dachfläche',
    annualConsumption: 'Jährlicher Stromverbrauch',
    roofOrientation: 'Dachausrichtung',
    roofAngle: 'Dachneigung',
    withBattery: 'Mit Batteriespeicher',
    calculate: 'Ersparnis berechnen',
    calculating: 'Berechne...',
    annualSavings: 'Jährliche Ersparnis',
    perMonth: '/Monat',
    recommendedSize: 'Empfohlene Größe',
    perYear: 'kWh/Jahr',
    co2Saved: 'CO₂ gespart/Jahr',
    payback: 'Amortisation',
    showDetails: 'Details anzeigen',
    estInvestment: 'Geschätzte Investition',
    selfConsumption: 'Eigenverbrauchsquote',
    afterSubsidy: 'Nach Förderung (bis 30%)',
    enterData: 'Geben Sie Ihre Daten ein und klicken Sie auf "Berechnen"',
    yourBenefits: 'Ihre Vorteile',
    whyWorth: 'Warum sich eine Photovoltaikanlage für Sie lohnt',
    savingsUp: 'Bis zu 70% Ersparnis',
    savingsDesc: 'Reduzieren Sie Ihre Stromkosten drastisch mit eigenem Solarstrom',
    warrantyTitle: '25 Jahre Garantie',
    warrantyDesc: 'Langfristige Sicherheit mit Herstellergarantie auf alle Module',
    co2Neutral: 'CO₂ Neutral',
    co2Desc: 'Schützen Sie die Umwelt mit 100% sauberer Energie',
    valueIncrease: 'Wertsteigerung',
    valueDesc: 'Erhöhen Sie den Wert Ihrer Immobilie nachhaltig',
    independence: 'Unabhängigkeit',
    indepDesc: 'Werden Sie unabhängig von steigenden Strompreisen',
    subsidy: 'Förderung',
    subsidyDesc: 'Profitieren Sie von attraktiven staatlichen Förderungen',
    ourProjects: 'Unsere Projekte',
    projectsDesc: 'Entdecken Sie einige unserer erfolgreich abgeschlossenen Photovoltaik-Installationen',
    smartSystem: 'Das intelligente Energiesystem',
    smartSystemDesc: 'Entdecken Sie, wie Photovoltaik, Speicher und E-Mobilität perfekt zusammenarbeiten',
    completeSystem: 'Komplettes Energiesystem',
    completeDesc: 'Ihr Zuhause wird zur eigenen Kraftwerk: Solaranlage, Batteriespeicher und Wallbox arbeiten intelligent zusammen für maximale Unabhängigkeit.',
    selfConsump80: 'Eigenverbrauch bis 80%',
    backupPower: 'Notstromversorgung',
    chargeEV: 'E-Auto laden',
    smartControl: 'Intelligente Steuerung',
    smartControlDesc: 'Überwachen und steuern Sie Ihre gesamte Energieanlage bequem per App - jederzeit und von überall.',
    realtime: 'Echtzeit-Monitoring',
    analysis: 'Verbrauchsanalyse',
    remote: 'Fernsteuerung',
    dayNight: 'Tag und Nacht versorgt',
    dayNightDesc: 'Mit Batteriespeicher nutzen Sie Ihren Solarstrom auch nach Sonnenuntergang. Laden Sie Ihr E-Auto mit selbst erzeugtem Strom.',
    supply247: '24/7 Eigenversorgung',
    nightUse: 'Nachtnutzung',
    freeCharging: 'Kostenlos tanken',
    sustainable: 'Nachhaltige Zukunft',
    sustainableDesc: 'Investieren Sie in saubere Energie und profitieren Sie von steigenden Strompreisen - gut für Ihr Portemonnaie und die Umwelt.',
    futureProof: 'Zukunftssicher',
    readyForSolar: 'Bereit für Ihre eigene Solaranlage?',
    readyDesc: 'Lassen Sie sich kostenlos und unverbindlich beraten. Wir erstellen Ihnen ein individuelles Angebot.',
    requestConsult: 'Kostenlose Beratung anfragen',
    south: 'Süden (optimal)',
    east: 'Osten',
    west: 'Westen',
    north: 'Norden',
    years: 'Jahre'
  },
  en: {
    badge: 'Photovoltaics of the Future',
    heroTitle: 'Solar Energy for',
    heroTitle2: 'Your Home',
    heroDesc: 'Discover our premium solar modules from Aiko, Bosch and SIG Energy. With cutting-edge technology and up to 25 years warranty.',
    freeConsult: 'Free Consultation',
    calcSavings: 'Calculate Savings',
    yearsWarranty: 'Years Warranty',
    efficiency: 'Efficiency',
    installations: 'Installations',
    premiumBrands: 'Our Premium Brands',
    premiumBrandsDesc: 'We only work with the best manufacturers for your solar system',
    solarCalc: 'Solar Calculator',
    calcPotential: 'Calculate your savings potential in seconds',
    roofArea: 'Roof Area',
    annualConsumption: 'Annual Power Consumption',
    roofOrientation: 'Roof Orientation',
    roofAngle: 'Roof Angle',
    withBattery: 'With Battery Storage',
    calculate: 'Calculate Savings',
    calculating: 'Calculating...',
    annualSavings: 'Annual Savings',
    perMonth: '/Month',
    recommendedSize: 'Recommended Size',
    perYear: 'kWh/Year',
    co2Saved: 'CO₂ saved/Year',
    payback: 'Payback Period',
    showDetails: 'Show Details',
    estInvestment: 'Estimated Investment',
    selfConsumption: 'Self-consumption Rate',
    afterSubsidy: 'After Subsidy (up to 30%)',
    enterData: 'Enter your data and click "Calculate"',
    yourBenefits: 'Your Benefits',
    whyWorth: 'Why a photovoltaic system is worth it for you',
    savingsUp: 'Up to 70% Savings',
    savingsDesc: 'Drastically reduce your electricity costs with your own solar power',
    warrantyTitle: '25 Years Warranty',
    warrantyDesc: 'Long-term security with manufacturer warranty on all modules',
    co2Neutral: 'CO₂ Neutral',
    co2Desc: 'Protect the environment with 100% clean energy',
    valueIncrease: 'Value Increase',
    valueDesc: 'Sustainably increase the value of your property',
    independence: 'Independence',
    indepDesc: 'Become independent from rising electricity prices',
    subsidy: 'Subsidy',
    subsidyDesc: 'Benefit from attractive government subsidies',
    ourProjects: 'Our Projects',
    projectsDesc: 'Discover some of our successfully completed photovoltaic installations',
    smartSystem: 'The Intelligent Energy System',
    smartSystemDesc: 'Discover how photovoltaics, storage and e-mobility work perfectly together',
    completeSystem: 'Complete Energy System',
    completeDesc: 'Your home becomes its own power plant: Solar system, battery storage and wallbox work intelligently together for maximum independence.',
    selfConsump80: 'Self-consumption up to 80%',
    backupPower: 'Backup Power',
    chargeEV: 'Charge EV',
    smartControl: 'Smart Control',
    smartControlDesc: 'Monitor and control your entire energy system conveniently via app - anytime and from anywhere.',
    realtime: 'Real-time Monitoring',
    analysis: 'Consumption Analysis',
    remote: 'Remote Control',
    dayNight: 'Day and Night Supply',
    dayNightDesc: 'With battery storage, you can use your solar power even after sunset. Charge your e-car with self-generated electricity.',
    supply247: '24/7 Self-supply',
    nightUse: 'Night Use',
    freeCharging: 'Free Charging',
    sustainable: 'Sustainable Future',
    sustainableDesc: 'Invest in clean energy and benefit from rising electricity prices - good for your wallet and the environment.',
    futureProof: 'Future-proof',
    readyForSolar: 'Ready for Your Own Solar System?',
    readyDesc: 'Get free and non-binding advice. We will create an individual offer for you.',
    requestConsult: 'Request Free Consultation',
    south: 'South (optimal)',
    east: 'East',
    west: 'West',
    north: 'North',
    years: 'Years'
  }
};

// Hero Section with Video Background
const PhotovoltaikHero = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const t = pvTranslations[language] || pvTranslations.de;

  return (
    <section className="relative h-screen min-h-[700px] overflow-hidden">
      {/* Background Image */}
      <div className="absolute inset-0 w-full h-full">
        <img
          src="https://customer-assets.emergentagent.com/job_8c2b47f1-6d08-4b4c-9016-67db6d0453b6/artifacts/olb5ab4t_IMG_0072.webp"
          alt="Solar Panels"
          className="w-full h-full object-cover"
        />
        {/* Dark Overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-slate-900/85 via-slate-900/70 to-slate-900/50"></div>
      </div>

      {/* Content */}
      <div className="relative z-10 h-full flex items-center">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-amber-500/20 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
              <Sun className="w-5 h-5 text-amber-400" />
              <span className="text-amber-300 text-sm font-medium">{t.badge}</span>
            </div>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              {t.heroTitle}{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-sky-400">
                {t.heroTitle2}
              </span>
            </h1>
            
            <p className="text-lg sm:text-xl text-gray-300 mb-8 leading-relaxed">
              {t.heroDesc}
            </p>

            <div className="flex flex-col sm:flex-row gap-4 mb-12">
              <Button 
                onClick={() => navigate('/beratung')}
                size="lg"
                className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white px-8 py-6 text-lg group shadow-xl shadow-amber-500/25"
              >
                {t.freeConsult}
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Button>
              <Button 
                onClick={() => document.getElementById('rechner').scrollIntoView({ behavior: 'smooth' })}
                size="lg"
                variant="outline"
                className="border-2 border-white/30 text-white hover:bg-white/10 backdrop-blur-sm px-8 py-6 text-lg"
              >
                <Calculator className="mr-2 w-5 h-5" />
                {t.calcSavings}
              </Button>
            </div>

            <div className="grid grid-cols-3 gap-6">
              <div className="text-center">
                <div className="text-3xl sm:text-4xl font-bold text-white mb-1">25+</div>
                <div className="text-sm text-gray-400">{t.yearsWarranty}</div>
              </div>
              <div className="text-center">
                <div className="text-3xl sm:text-4xl font-bold text-white mb-1">22%</div>
                <div className="text-sm text-gray-400">{t.efficiency}</div>
              </div>
              <div className="text-center">
                <div className="text-3xl sm:text-4xl font-bold text-white mb-1">2500+</div>
                <div className="text-sm text-gray-400">{t.installations}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-10 animate-bounce">
        <div className="w-6 h-10 border-2 border-white/50 rounded-full flex items-start justify-center p-2">
          <div className="w-1 h-3 bg-white/70 rounded-full"></div>
        </div>
      </div>
    </section>
  );
};

// Product Brands Showcase
const BrandsShowcase = () => {
  const { language } = useLanguage();
  const t = pvTranslations[language] || pvTranslations.de;
  
  const brands = language === 'en' ? [
    {
      name: 'Aiko',
      logo: 'https://customer-assets.emergentagent.com/job_4471c19e-bde9-4b97-9a10-805621e3566d/artifacts/sg9160pk_IMG_7643.jpeg',
      description: 'Premium N-Type modules with up to 22.6% efficiency',
      features: ['25 year warranty', 'Bifacial technology', 'Anti-LID & Anti-PID']
    },
    {
      name: 'Longi',
      logo: 'https://customer-assets.emergentagent.com/job_8c2b47f1-6d08-4b4c-9016-67db6d0453b6/artifacts/5i71mpbv_IMG_0070.png',
      description: 'World market leader for monocrystalline solar modules',
      features: ['Hi-MO 6 Technology', '22.8% efficiency', '25 year warranty']
    },
    {
      name: 'SIG Energy',
      logo: 'https://customer-assets.emergentagent.com/job_4471c19e-bde9-4b97-9a10-805621e3566d/artifacts/9qoy3mgg_IMG_7644.jpeg',
      description: 'Intelligent storage solutions for your independence',
      features: ['Backup power function', 'App control', '15 year warranty']
    },
    {
      name: 'EcoFlow',
      logo: 'https://customer-assets.emergentagent.com/job_8c2b47f1-6d08-4b4c-9016-67db6d0453b6/artifacts/5hqehss0_IMG_0077.jpeg',
      description: 'Portable power stations and home storage',
      features: ['Fast charging', 'Modular system', 'Smart Home integration']
    }
  ] : [
    {
      name: 'Aiko',
      logo: 'https://customer-assets.emergentagent.com/job_4471c19e-bde9-4b97-9a10-805621e3566d/artifacts/sg9160pk_IMG_7643.jpeg',
      description: 'Premium N-Type Module mit bis zu 22,6% Wirkungsgrad',
      features: ['25 Jahre Garantie', 'Bifaziale Technologie', 'Anti-LID & Anti-PID']
    },
    {
      name: 'Longi',
      logo: 'https://customer-assets.emergentagent.com/job_8c2b47f1-6d08-4b4c-9016-67db6d0453b6/artifacts/5i71mpbv_IMG_0070.png',
      description: 'Weltmarktführer für monokristalline Solarmodule',
      features: ['Hi-MO 6 Technologie', '22,8% Wirkungsgrad', '25 Jahre Garantie']
    },
    {
      name: 'SIG Energy',
      logo: 'https://customer-assets.emergentagent.com/job_4471c19e-bde9-4b97-9a10-805621e3566d/artifacts/9qoy3mgg_IMG_7644.jpeg',
      description: 'Intelligente Speicherlösungen für Ihre Unabhängigkeit',
      features: ['Notstromfunktion', 'App-Steuerung', '15 Jahre Garantie']
    },
    {
      name: 'EcoFlow',
      logo: 'https://customer-assets.emergentagent.com/job_8c2b47f1-6d08-4b4c-9016-67db6d0453b6/artifacts/5hqehss0_IMG_0077.jpeg',
      description: 'Portable Powerstations und Heimspeicher',
      features: ['Schnellladung', 'Modulares System', 'Smart Home Integration']
    }
  ];

  return (
    <section className="py-20 bg-gradient-to-br from-slate-50 to-blue-50">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
            {t.premiumBrands.split(' ')[0]} <span className="text-transparent bg-clip-text bg-gradient-to-r from-sky-600 to-amber-500">{t.premiumBrands.split(' ').slice(1).join(' ')}</span>
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            {t.premiumBrandsDesc}
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {brands.map((brand, index) => (
            <Card key={index} className="group hover:shadow-2xl transition-all duration-300 border-0 bg-white overflow-hidden">
              <div className="h-48 overflow-hidden">
                <img 
                  src={brand.logo} 
                  alt={brand.name}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
              </div>
              <CardContent className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-2">{brand.name}</h3>
                <p className="text-gray-600 text-sm mb-4">{brand.description}</p>
                <ul className="space-y-2">
                  {brand.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center text-sm text-gray-700">
                      <CheckCircle2 className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};

// Solar Calculator Component
const SolarCalculator = () => {
  const { language } = useLanguage();
  const t = pvTranslations[language] || pvTranslations.de;
  
  const [formData, setFormData] = useState({
    roof_area: 50,
    annual_consumption: 4000,
    roof_orientation: 'sued',
    roof_angle: 30,
    has_battery: false
  });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : type === 'number' ? Number(value) : value
    }));
  };

  const calculateSavings = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/solar-calculator`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      if (!response.ok) throw new Error('Calculation failed');
      const data = await response.json();
      setResult(data);
      setShowDetails(true);
    } catch (error) {
      const fallbackResult = {
        recommended_system_size: Math.round(formData.roof_area / 6 * 10) / 10,
        annual_production: Math.round(formData.roof_area / 6 * 950),
        self_consumption_rate: formData.has_battery ? 70 : 30,
        annual_savings: Math.round(formData.annual_consumption * 0.35 * (formData.has_battery ? 0.7 : 0.3)),
        co2_savings: Math.round(formData.roof_area / 6 * 950 * 0.4),
        payback_period: 10,
        total_investment: Math.round(formData.roof_area / 6 * 1400) + (formData.has_battery ? 8000 : 0),
        monthly_savings: Math.round(formData.annual_consumption * 0.35 * (formData.has_battery ? 0.7 : 0.3) / 12)
      };
      setResult(fallbackResult);
      setShowDetails(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="rechner" className="py-20 bg-gradient-to-br from-sky-900 to-slate-900">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
            <Calculator className="inline w-10 h-10 mr-3 text-amber-400" />
            {t.solarCalc.split('-')[0]}<span className="text-amber-400">{t.solarCalc.split('-')[1] || 'Rechner'}</span>
          </h2>
          <p className="text-lg text-gray-300 max-w-2xl mx-auto">{t.calcPotential}</p>
        </div>

        <div className="max-w-4xl mx-auto">
          <Card className="bg-white/10 backdrop-blur-lg border-white/20">
            <CardContent className="p-8">
              <div className="grid md:grid-cols-2 gap-8">
                <div className="space-y-6">
                  <div>
                    <label className="block text-white text-sm font-medium mb-2">{t.roofArea} (m²)</label>
                    <input type="range" name="roof_area" min="20" max="200" value={formData.roof_area} onChange={handleChange} className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-amber-500" />
                    <div className="text-amber-400 font-bold text-xl mt-1">{formData.roof_area} m²</div>
                  </div>
                  <div>
                    <label className="block text-white text-sm font-medium mb-2">{t.annualConsumption} (kWh)</label>
                    <input type="range" name="annual_consumption" min="1000" max="15000" step="500" value={formData.annual_consumption} onChange={handleChange} className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-amber-500" />
                    <div className="text-amber-400 font-bold text-xl mt-1">{formData.annual_consumption.toLocaleString()} kWh</div>
                  </div>
                  <div>
                    <label className="block text-white text-sm font-medium mb-2">{t.roofOrientation}</label>
                    <select name="roof_orientation" value={formData.roof_orientation} onChange={handleChange} className="w-full bg-gray-800 text-white rounded-lg px-4 py-3 border border-gray-600 focus:border-amber-500 focus:outline-none">
                      <option value="sued">{t.south}</option>
                      <option value="ost">{t.east}</option>
                      <option value="west">{t.west}</option>
                      <option value="nord">{t.north}</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-white text-sm font-medium mb-2">{t.roofAngle}: {formData.roof_angle}°</label>
                    <input type="range" name="roof_angle" min="0" max="60" value={formData.roof_angle} onChange={handleChange} className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-amber-500" />
                  </div>
                  <div className="flex items-center gap-3">
                    <input type="checkbox" name="has_battery" checked={formData.has_battery} onChange={handleChange} className="w-5 h-5 accent-amber-500" />
                    <label className="text-white font-medium flex items-center gap-2">
                      <Battery className="w-5 h-5 text-amber-400" />
                      {t.withBattery}
                    </label>
                  </div>
                  <Button onClick={calculateSavings} disabled={loading} className="w-full bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white py-6 text-lg font-bold">
                    {loading ? t.calculating : t.calculate}
                  </Button>
                </div>

                <div className="space-y-4">
                  {result ? (
                    <>
                      <div className="bg-gradient-to-br from-amber-500 to-amber-600 rounded-xl p-6 text-center">
                        <div className="text-white/80 text-sm mb-1">{t.annualSavings}</div>
                        <div className="text-4xl font-bold text-white">{result.annual_savings.toLocaleString()} €</div>
                        <div className="text-white/80 text-sm mt-1">({result.monthly_savings} €{t.perMonth})</div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="bg-white/10 rounded-xl p-4 text-center">
                          <Sun className="w-8 h-8 text-amber-400 mx-auto mb-2" />
                          <div className="text-2xl font-bold text-white">{result.recommended_system_size} kWp</div>
                          <div className="text-gray-400 text-xs">{t.recommendedSize}</div>
                        </div>
                        <div className="bg-white/10 rounded-xl p-4 text-center">
                          <Zap className="w-8 h-8 text-sky-400 mx-auto mb-2" />
                          <div className="text-2xl font-bold text-white">{result.annual_production.toLocaleString()}</div>
                          <div className="text-gray-400 text-xs">{t.perYear}</div>
                        </div>
                        <div className="bg-white/10 rounded-xl p-4 text-center">
                          <Leaf className="w-8 h-8 text-green-400 mx-auto mb-2" />
                          <div className="text-2xl font-bold text-white">{result.co2_savings.toLocaleString()} kg</div>
                          <div className="text-gray-400 text-xs">{t.co2Saved}</div>
                        </div>
                        <div className="bg-white/10 rounded-xl p-4 text-center">
                          <BarChart3 className="w-8 h-8 text-purple-400 mx-auto mb-2" />
                          <div className="text-2xl font-bold text-white">{result.payback_period} {t.years}</div>
                          <div className="text-gray-400 text-xs">{t.payback}</div>
                        </div>
                      </div>
                      <button onClick={() => setShowDetails(!showDetails)} className="flex items-center justify-center gap-2 text-amber-400 hover:text-amber-300 w-full py-2">
                        {showDetails ? <ChevronUp /> : <ChevronDown />}
                        {t.showDetails}
                      </button>
                      {showDetails && (
                        <div className="bg-white/5 rounded-xl p-4 space-y-2 text-sm">
                          <div className="flex justify-between text-gray-300">
                            <span>{t.estInvestment}:</span>
                            <span className="text-white font-bold">{result.total_investment.toLocaleString()} €</span>
                          </div>
                          <div className="flex justify-between text-gray-300">
                            <span>{t.selfConsumption}:</span>
                            <span className="text-white font-bold">{result.self_consumption_rate}%</span>
                          </div>
                          <div className="flex justify-between text-gray-300">
                            <span>{t.afterSubsidy}:</span>
                            <span className="text-green-400 font-bold">ab {Math.round(result.total_investment * 0.7).toLocaleString()} €</span>
                          </div>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="h-full flex items-center justify-center">
                      <div className="text-center text-gray-400">
                        <Calculator className="w-16 h-16 mx-auto mb-4 opacity-50" />
                        <p>{t.enterData}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};

// AI Chatbot Component
const SolarChatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: 'assistant', content: 'Hallo! 👋 Ich bin Ihr virtueller Solar-Berater. Wie kann ich Ihnen heute helfen?' }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!input.trim() || loading) return;

    const userMessage = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setLoading(true);

    try {
      const response = await fetch(`${BACKEND_URL}/api/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: userMessage,
          session_id: sessionId,
          history: messages.slice(-10) // Send last 10 messages for context
        })
      });

      if (!response.ok) throw new Error('Chat failed');

      const data = await response.json();
      setSessionId(data.session_id);
      setMessages(prev => [...prev, { role: 'assistant', content: data.response }]);
    } catch (error) {
      console.error('Chat error:', error);
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: 'Entschuldigung, es gab einen Fehler. Bitte versuchen Sie es erneut oder kontaktieren Sie uns direkt unter +49 157 82283963.' 
      }]);
    } finally {
      setLoading(false);
    }
  };

  const quickQuestions = [
    'Was kostet eine Solaranlage?',
    'Wie viel kann ich sparen?',
    'Welche Förderungen gibt es?'
  ];

  return (
    <>
      {/* Chat Button */}
      <button
        onClick={() => setIsOpen(true)}
        className={`fixed bottom-20 right-6 z-40 bg-gradient-to-r from-amber-500 to-amber-600 text-white p-4 rounded-full shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-110 ${isOpen ? 'hidden' : 'flex'} items-center gap-2`}
      >
        <MessageCircle className="w-6 h-6" />
        <span className="hidden sm:inline font-medium">Solar-Berater</span>
      </button>

      {/* Chat Window */}
      {isOpen && (
        <div className="fixed bottom-20 right-6 z-50 w-full max-w-md">
          <Card className="shadow-2xl border-0 overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-sky-600 to-sky-700 p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                  <Sun className="w-6 h-6 text-amber-300" />
                </div>
                <div>
                  <h3 className="text-white font-bold">Solar-Berater AI</h3>
                  <div className="flex items-center gap-1 text-green-300 text-xs">
                    <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                    Online
                  </div>
                </div>
              </div>
              <button onClick={() => setIsOpen(false)} className="text-white/80 hover:text-white">
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Messages */}
            <div className="h-80 overflow-y-auto p-4 bg-gray-50 space-y-4">
              {messages.map((msg, index) => (
                <div key={index} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[80%] p-3 rounded-2xl ${
                    msg.role === 'user' 
                      ? 'bg-sky-600 text-white rounded-br-none' 
                      : 'bg-white shadow-sm rounded-bl-none'
                  }`}>
                    <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                  </div>
                </div>
              ))}
              {loading && (
                <div className="flex justify-start">
                  <div className="bg-white shadow-sm rounded-2xl rounded-bl-none p-3">
                    <div className="flex gap-1">
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce delay-100"></div>
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce delay-200"></div>
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Quick Questions */}
            {messages.length === 1 && (
              <div className="px-4 py-2 bg-gray-100 flex gap-2 overflow-x-auto">
                {quickQuestions.map((q, i) => (
                  <button
                    key={i}
                    onClick={() => setInput(q)}
                    className="text-xs bg-white px-3 py-1.5 rounded-full border border-gray-200 hover:border-sky-500 hover:text-sky-600 whitespace-nowrap transition-colors"
                  >
                    {q}
                  </button>
                ))}
              </div>
            )}

            {/* Input */}
            <form onSubmit={sendMessage} className="p-4 bg-white border-t flex gap-2">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ihre Frage..."
                className="flex-1 px-4 py-2 border rounded-full focus:outline-none focus:border-sky-500"
                disabled={loading}
              />
              <button
                type="submit"
                disabled={loading || !input.trim()}
                className="bg-sky-600 text-white p-2 rounded-full hover:bg-sky-700 disabled:opacity-50 transition-colors"
              >
                <Send className="w-5 h-5" />
              </button>
            </form>
          </Card>
        </div>
      )}
    </>
  );
};

// Benefits Section
const PhotovoltaikVorteile = () => {
  const { language } = useLanguage();
  const t = pvTranslations[language] || pvTranslations.de;
  
  const vorteile = [
    { icon: TrendingDown, title: t.savingsUp, description: t.savingsDesc },
    { icon: Shield, title: t.warrantyTitle, description: t.warrantyDesc },
    { icon: Leaf, title: t.co2Neutral, description: t.co2Desc },
    { icon: Home, title: t.valueIncrease, description: t.valueDesc },
    { icon: Zap, title: t.independence, description: t.indepDesc },
    { icon: Award, title: t.subsidy, description: t.subsidyDesc }
  ];

  return (
    <section className="py-20 bg-white">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
            {t.yourBenefits.split(' ')[0]} <span className="text-transparent bg-clip-text bg-gradient-to-r from-sky-600 to-amber-500">{t.yourBenefits.split(' ').slice(1).join(' ')}</span>
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            {t.whyWorth}
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {vorteile.map((vorteil, index) => {
            const Icon = vorteil.icon;
            return (
              <Card key={index} className="group hover:shadow-xl transition-all duration-300 border-2 border-transparent hover:border-sky-200">
                <CardContent className="p-8">
                  <div className="w-16 h-16 bg-gradient-to-br from-sky-100 to-amber-100 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                    <Icon className="w-8 h-8 text-sky-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{vorteil.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{vorteil.description}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// Projects Gallery Section - Unsere Projekte
const ProjectsGallery = () => {
  const { language } = useLanguage();
  const t = pvTranslations[language] || pvTranslations.de;
  
  const projects = language === 'en' ? [
    { image: 'https://customer-assets.emergentagent.com/job_8c2b47f1-6d08-4b4c-9016-67db6d0453b6/artifacts/qlkbrerr_2025-11-07-11-16-34-781.png', title: 'Single Family House Stuttgart', description: '12 kWp system with storage' },
    { image: 'https://customer-assets.emergentagent.com/job_8c2b47f1-6d08-4b4c-9016-67db6d0453b6/artifacts/va5ikjvl_PHOTO-2025-12-06-10-09-21.jpeg', title: 'Multi-Family House Munich', description: '18 kWp complete solution' },
    { image: 'https://customer-assets.emergentagent.com/job_8c2b47f1-6d08-4b4c-9016-67db6d0453b6/artifacts/2gs9uyce_2025-11-29-14-25-40-868.png', title: 'Semi-Detached House Berlin', description: '10 kWp with Wallbox' }
  ] : [
    { image: 'https://customer-assets.emergentagent.com/job_8c2b47f1-6d08-4b4c-9016-67db6d0453b6/artifacts/qlkbrerr_2025-11-07-11-16-34-781.png', title: 'Einfamilienhaus Stuttgart', description: '12 kWp Anlage mit Speicher' },
    { image: 'https://customer-assets.emergentagent.com/job_8c2b47f1-6d08-4b4c-9016-67db6d0453b6/artifacts/va5ikjvl_PHOTO-2025-12-06-10-09-21.jpeg', title: 'Mehrfamilienhaus München', description: '18 kWp Komplettlösung' },
    { image: 'https://customer-assets.emergentagent.com/job_8c2b47f1-6d08-4b4c-9016-67db6d0453b6/artifacts/2gs9uyce_2025-11-29-14-25-40-868.png', title: 'Doppelhaushälfte Berlin', description: '10 kWp mit Wallbox' }
  ];

  const [selectedImage, setSelectedImage] = useState(null);

  return (
    <section className="py-20 bg-gradient-to-br from-slate-50 to-blue-50">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
            {t.ourProjects.split(' ')[0]} <span className="text-transparent bg-clip-text bg-gradient-to-r from-sky-600 to-amber-500">{t.ourProjects.split(' ').slice(1).join(' ')}</span>
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            {t.projectsDesc}
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          {projects.map((project, index) => (
            <div key={index} className="group cursor-pointer" onClick={() => setSelectedImage(project)}>
              <div className="aspect-square rounded-xl overflow-hidden shadow-lg bg-gray-100">
                <img src={project.image} alt={project.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              </div>
              <div className="mt-4 text-center">
                <h3 className="text-xl font-bold text-gray-900">{project.title}</h3>
                <p className="text-gray-600">{project.description}</p>
              </div>
            </div>
          ))}
        </div>

        {selectedImage && (
          <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4" onClick={() => setSelectedImage(null)}>
            <button className="absolute top-4 right-4 text-white hover:text-gray-300"><X className="w-8 h-8" /></button>
            <div className="max-w-4xl w-full" onClick={e => e.stopPropagation()}>
              <img src={selectedImage.image} alt={selectedImage.title} className="w-full h-auto rounded-xl" />
              <div className="text-center mt-4">
                <h3 className="text-2xl font-bold text-white">{selectedImage.title}</h3>
                <p className="text-gray-300">{selectedImage.description}</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

// Smart Energy System Section
const SmartEnergySystem = () => {
  const { language } = useLanguage();
  const t = pvTranslations[language] || pvTranslations.de;
  
  const features = [
    {
      image: 'https://customer-assets.emergentagent.com/job_8c2b47f1-6d08-4b4c-9016-67db6d0453b6/artifacts/51dw8cpt_IMG_0078.png',
      title: t.completeSystem,
      description: t.completeDesc,
      features: [t.selfConsump80, t.backupPower, t.chargeEV]
    },
    {
      image: 'https://customer-assets.emergentagent.com/job_8c2b47f1-6d08-4b4c-9016-67db6d0453b6/artifacts/3pwb02yu_IMG_0076.jpeg',
      title: t.smartControl,
      description: t.smartControlDesc,
      features: [t.realtime, t.analysis, t.remote]
    },
    {
      image: 'https://customer-assets.emergentagent.com/job_8c2b47f1-6d08-4b4c-9016-67db6d0453b6/artifacts/bjqu4o6s_IMG_0075.jpeg',
      title: t.dayNight,
      description: t.dayNightDesc,
      features: [t.supply247, t.nightUse, t.freeCharging]
    },
    {
      image: 'https://customer-assets.emergentagent.com/job_8c2b47f1-6d08-4b4c-9016-67db6d0453b6/artifacts/kt4xy72r_IMG_0071.jpeg',
      title: t.sustainable,
      description: t.sustainableDesc,
      features: [t.co2Neutral, t.valueIncrease, t.futureProof]
    }
  ];

  return (
    <section className="py-20 bg-gradient-to-br from-sky-900 to-slate-900">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
            {language === 'en' ? 'The ' : 'Das '}<span className="text-amber-400">{language === 'en' ? 'Intelligent' : 'intelligente'}</span> {language === 'en' ? 'Energy System' : 'Energiesystem'}
          </h2>
          <p className="text-lg text-gray-300 max-w-3xl mx-auto">{t.smartSystemDesc}</p>
        </div>

        <div className="space-y-16">
          {features.map((feature, index) => (
            <div key={index} className={`grid lg:grid-cols-2 gap-12 items-center ${index % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}>
              <div className={`${index % 2 === 1 ? 'lg:order-2' : ''}`}>
                <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                  <img src={feature.image} alt={feature.title} className="w-full h-auto" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent"></div>
                </div>
              </div>
              <div className={`${index % 2 === 1 ? 'lg:order-1' : ''}`}>
                <h3 className="text-2xl sm:text-3xl font-bold text-white mb-4">{feature.title}</h3>
                <p className="text-lg text-gray-300 mb-6 leading-relaxed">{feature.description}</p>
                <div className="flex flex-wrap gap-3">
                  {feature.features.map((item, idx) => (
                    <span key={idx} className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full text-white text-sm">
                      <CheckCircle2 className="w-4 h-4 text-amber-400" />
                      {item}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// CTA Section
const PhotovoltaikCTA = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const t = pvTranslations[language] || pvTranslations.de;

  return (
    <section className="py-20 bg-gradient-to-r from-amber-500 to-amber-600">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center text-white">
          <Euro className="w-16 h-16 mx-auto mb-6 opacity-80" />
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6">{t.readyForSolar}</h2>
          <p className="text-xl mb-8 leading-relaxed opacity-90">{t.readyDesc}</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button onClick={() => navigate('/beratung')} size="lg" className="bg-white text-amber-600 hover:bg-gray-100 px-8 py-6 text-lg font-bold shadow-xl">
              {t.requestConsult}
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
            <Button onClick={() => document.getElementById('rechner').scrollIntoView({ behavior: 'smooth' })} size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white/10 px-8 py-6 text-lg">
              <Calculator className="mr-2 w-5 h-5" />
              {t.calcSavings}
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

// Main Page Component
const PhotovoltaikPage = () => {
  return (
    <div className="min-h-screen">
      <Header />
      <PhotovoltaikHero />
      <BrandsShowcase />
      <SmartEnergySystem />
      <SolarCalculator />
      <PhotovoltaikVorteile />
      <ProjectsGallery />
      <PhotovoltaikCTA />
      <Contact />
      <Footer />
    </div>
  );
};

export default PhotovoltaikPage;
